// @flow
import React, { Fragment, useContext } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { windowWrapper } from '@clubmed/utils';

import { t } from 'Src/i18n';
import ReactAppContext from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import DynamicLinkUtil from 'Components/frameworks/dynamic-link/DynamicLinkUtil';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';

import { Button } from 'Components/frameworks/button/Button.jsx';
import { OfferDiscount } from 'Components/widgets/offer-discount/OfferDiscount.jsx';
import { useRouter } from 'Client/router/useRouter';
import {
  generateLinkProp,
  generateProductLink,
  generateCmtaLinkProp,
  type ProductProps,
} from 'Components/widgets/product/Product.utils';
import { ProductChildcareAvailabilities } from './product-childcare-availabilities/productChildcareAvailabilities.jsx';
import { ProductComparison } from './product-comparison/ProductComparison.jsx';
import { ProductFlag } from './product-flag/ProductFlag.jsx';
import { ProductHeader } from './product-header/ProductHeader.jsx';
import { ProductThumbnail } from './product-thumbnail/ProductThumbnail.jsx';

type Props = {|
  ...$Exact<ProductProps>,
  offer?: Offer,
  onComparisonClick?: () => mixed,
  onClick?: (SyntheticEvent<HTMLElement>) => void,
|};

export const Product = ({
  callToAction = {},
  childcare,
  className,
  comparison,
  flag,
  hasComparison,
  hasDiscount,
  hasLazyLoading,
  header,
  isLinkDisabled,
  offer,
  offerDiscount,
  onClick,
  onComparisonClick,
  shouldFadeInLong = true,
  thumbnail,
  url,
}: Props) => {
  const appContext = useContext(ReactAppContext);

  const { history } = useRouter<{}, SearchQueryParams>();

  const ProductHeaderWrapper = hasComparison ? ProductComparison : Fragment;
  const linkShouldBeDisabled = isLinkDisabled || hasComparison;

  const goOnResort = () => {
    const newHistoryState = new DynamicLinkUtil(
      windowWrapper.getLocation(),
      url ? generateProductLink(url, offer) : '',
    );

    history.pushRoute(newHistoryState.toString());
  };

  const productHeader = (
    <ProductHeader
      {...header}
      className="pt-5"
      hasRelativeElement
      isLinkDisabled={linkShouldBeDisabled}
      link={
        appContext.isCmta
          ? generateCmtaLinkProp(url, appContext.originalUrl)
          : generateLinkProp(url, offer)
      }
    />
  );

  return (
    <article
      className={classNames(className, 'fadeInLong flex flex-col relative', {
        fadeInLong: shouldFadeInLong,
      })}
      onClick={onClick}
    >
      <ProductHeaderWrapper
        {...(hasComparison ? { ...comparison, onComparisonClick: onComparisonClick } : {})}
      >
        <div className="relative" data-testid="Product-Header-Wrapper">
          {hasDiscount && (
            // $FlowFixMe
            <OfferDiscount {...offerDiscount} className="absolute opacity-75 inset-x-0 z-1" />
          )}
          {/* $FlowFixMe */}
          <ProductThumbnail
            className={classNames('rounded-tiny', 'overflow-hidden')}
            {...thumbnail}
            hasLazyLoading={hasLazyLoading}
            isLinkDisabled
            nextLabel={t('common.next')}
            previousLabel={t('common.previous')}
          />
          {!hasComparison ? productHeader : null}
        </div>
      </ProductHeaderWrapper>
      {hasComparison ? productHeader : null}
      <ProductFlag {...flag} isLinkDisabled={linkShouldBeDisabled} onClick={goOnResort} />
      {!!callToAction.label && (
        <Button
          {...callToAction}
          className="mt-2 w-full"
          colorBg={linkShouldBeDisabled ? COLORS.INFO : COLORS.BLUE}
          colorBorder={linkShouldBeDisabled ? COLORS.INFO : COLORS.BLUE}
          iconRight={{ name: ICONS.BOLDER_ARROW_FORTH }}
          isDisabled={linkShouldBeDisabled}
          onClick={goOnResort}
        >
          {callToAction.label}
        </Button>
      )}
      {isFeatureFlipped(appContext)('faceting.childcare') &&
        childcare &&
        !isEmpty(childcare.items) && <ProductChildcareAvailabilities {...childcare} />}
    </article>
  );
};
